import type { DocumentExtractionV2SourceResourceType } from './documentExtractionV2Source';
import type { BulkDocumentExtractionV2InvokeResult } from './documentExtractionV2BulkInvoke';

export type DocumentExtractionAnalyticsSourceType =
  | 'document'
  | 'note'
  | 'survey';

export type DocumentExtractionRequestOutcome =
  | 'accepted'
  | 'partial'
  | 'duplicate'
  | 'error';

export type DocumentExtractionSelectionCountBucket = '1' | '2-5' | '6+';

export type DocumentExtractionReviewSurface =
  | 'member_clinical_tab'
  | 'extraction_modal'
  | 'extraction_tasks_audit'
  | 'labs_range_view'
  | 'ai_tools';

/**
 * Maps the bounded FHIR source resource category to the analytics sourceType.
 * Never derive this from a document name, title, MIME type, or resource ID.
 */
export const toDocumentExtractionAnalyticsSourceType = (
  sourceResourceType: DocumentExtractionV2SourceResourceType,
): DocumentExtractionAnalyticsSourceType => {
  if (sourceResourceType === 'Communication') {
    return 'note';
  }
  if (sourceResourceType === 'QuestionnaireResponse') {
    return 'survey';
  }
  return 'document';
};

export const bucketDocumentExtractionSelectionCount = (
  count: number,
): DocumentExtractionSelectionCountBucket => {
  if (count <= 1) {
    return '1';
  }
  if (count <= 5) {
    return '2-5';
  }
  return '6+';
};

/**
 * Classifies one clinician-triggered bulk invoke settlement from observable
 * started/skipped/failed counts. Skipped includes both local pre-checks and
 * HTTP 409 duplicate responses; the service does not expose a separate
 * "duplicate" count, so all-skipped with no failures is reported as
 * `duplicate`.
 */
export const classifyBulkDocumentExtractionOutcome = (
  result: BulkDocumentExtractionV2InvokeResult,
): DocumentExtractionRequestOutcome => {
  if (result.started > 0) {
    return result.failed === 0 && result.skipped === 0 ? 'accepted' : 'partial';
  }
  if (result.failed > 0) {
    return 'error';
  }
  if (result.skipped > 0) {
    return 'duplicate';
  }
  return 'error';
};
